import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import FlagIcon from '@mui/icons-material/Flag';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ClassificationChip, { PURPLE_SX } from '../common/ClassificationChip';
import ContradictionAlert from '../common/ContradictionAlert';
import SuggestionPageShell from '../common/SuggestionPageShell';
import SuggestionReview from '../common/SuggestionReview';
import HighlightedPolicy from '../common/HighlightedPolicy';
import EssayContent from '../EssayContent';
import {
  acceptModerationSuggestion,
  fetchCriteria,
  fetchPolicy,
  flagInaccurateModerations,
  generateModerationSuggestion,
} from '../../services/api';
import {
  Criteria,
  LabelType,
  LinkedModeration,
  ModerationPolicySuggestion,
  Policy,
} from '../../types';

const ModerationCard: React.FC<{
  moderation: LinkedModeration;
  selected: boolean;
  onToggleSelected: (airtableRecordId: string) => void;
  onClick: () => void;
}> = ({ moderation, selected, onToggleSelected, onClick }) => (
  <Paper
    variant="outlined"
    onClick={onClick}
    sx={{
      p: 1,
      pr: 6,
      flex: '0 0 560px',
      bgcolor: selected ? 'primary.50' : 'grey.50',
      borderColor: selected ? 'primary.main' : undefined,
      cursor: 'pointer',
      transition: 'background-color 0.15s',
      position: 'relative',
      '&:hover': { bgcolor: selected ? 'primary.100' : 'grey.100' },
    }}
  >
    <Tooltip title={selected ? 'Unmark as inaccurate' : 'Mark as inaccurate'}>
      <IconButton
        size="medium"
        color={selected ? 'primary' : 'default'}
        onClick={e => {
          e.stopPropagation();
          onToggleSelected(moderation.airtableRecordId);
        }}
        aria-label="Mark moderator feedback as inaccurate"
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: selected ? 'primary.main' : 'divider',
          '&:hover': { bgcolor: selected ? 'primary.50' : 'grey.100' },
        }}
      >
        {selected ? <FlagIcon fontSize="small" /> : <FlagOutlinedIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
    {moderation.contextForChange && (
      <>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 600, display: 'block' }}
        >
          Moderator Feedback
        </Typography>
        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, lineHeight: 1.4 }}>
          {moderation.contextForChange}
        </Typography>
      </>
    )}
    {moderation.originalComment && (
      <>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 600, display: 'block' }}
        >
          AI Comment
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
          {moderation.originalComment}
        </Typography>
      </>
    )}
  </Paper>
);

const FlagSelectedBar: React.FC<{
  count: number;
  flagging: boolean;
  onFlag: () => void;
}> = ({ count, flagging, onFlag }) => {
  if (count === 0) return null;
  return (
    <Box
      sx={{
        position: 'sticky',
        bottom: 0,
        display: 'flex',
        justifyContent: 'center',
        py: 1,
        bgcolor: 'background.paper',
        borderTop: '1px solid',
        borderColor: 'divider',
        zIndex: 2,
      }}
    >
      <Button variant="contained" startIcon={<FlagIcon />} onClick={onFlag} disabled={flagging}>
        {flagging ? 'Flagging...' : `Flag ${count} as inaccurate`}
      </Button>
    </Box>
  );
};

type Screen = 'select' | 'contradiction' | 'diff';

const ModerationPolicySuggestions: React.FC = () => {
  const [criteriaList, setCriteriaList] = useState<Criteria[]>([]);
  const [selectedCriteriaId, setSelectedCriteriaId] = useState('');
  const [currentPolicy, setCurrentPolicy] = useState<Policy | null>(null);
  const [suggestion, setSuggestion] = useState<ModerationPolicySuggestion | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState('');
  const [screen, setScreen] = useState<Screen>('select');
  const [dialogModeration, setDialogModeration] = useState<LinkedModeration | null>(null);
  const [selectedSentenceIndex, setSelectedSentenceIndex] = useState<number | null>(null);
  const [selectedToFlag, setSelectedToFlag] = useState<Set<string>>(new Set());
  const [flagging, setFlagging] = useState(false);

  const toggleSelectedToFlag = useCallback((airtableRecordId: string) => {
    setSelectedToFlag(prev => {
      const next = new Set(prev);
      if (next.has(airtableRecordId)) {
        next.delete(airtableRecordId);
      } else {
        next.add(airtableRecordId);
      }
      return next;
    });
  }, []);

  const handleOpenDialog = useCallback((mod: LinkedModeration) => {
    setDialogModeration(mod);
    setSelectedSentenceIndex(null);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialogModeration(null);
    setSelectedSentenceIndex(null);
  }, []);

  const handleSentenceClick = useCallback((_fragment: string, index: number) => {
    setSelectedSentenceIndex(prev => (prev === index ? null : index));
  }, []);

  const getLabelTypeForFragment = useCallback(
    (index: number): LabelType | null => {
      if (!dialogModeration?.sentences) return null;
      return dialogModeration.sentences[index]?.label as LabelType | null;
    },
    [dialogModeration]
  );

  const selectedSentence =
    selectedSentenceIndex !== null ? dialogModeration?.sentences[selectedSentenceIndex] : null;

  const loadData = useCallback(async () => {
    const criteria = await fetchCriteria();
    setCriteriaList(criteria);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const generatingRef = useRef(false);
  const policyRequestIdRef = useRef(0);

  const loadPolicy = useCallback((policyId: string) => {
    const requestId = ++policyRequestIdRef.current;
    fetchPolicy(policyId)
      .then(policy => {
        if (policyRequestIdRef.current === requestId) {
          setCurrentPolicy(policy);
        }
      })
      .catch(() => {
        if (policyRequestIdRef.current === requestId) {
          setCurrentPolicy(null);
        }
      });
  }, []);

  const generate = useCallback(async (criteriaId: string) => {
    if (generatingRef.current) return;
    generatingRef.current = true;
    setGenerating(true);
    setGenerateError('');
    setSelectedToFlag(new Set());
    try {
      const result = await generateModerationSuggestion(criteriaId);
      if ('noSuggestion' in result) {
        setSuggestion(null);
        setScreen('select');
      } else {
        setSuggestion(result);
        setScreen('contradiction');
      }
    } catch {
      setGenerateError('Failed to generate suggestion. Please try again.');
    } finally {
      setGenerating(false);
      generatingRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!selectedCriteriaId) {
      policyRequestIdRef.current++;
      setSuggestion(null);
      setCurrentPolicy(null);
      setScreen('select');
      return;
    }
    const criteria = criteriaList.find(c => c.id === selectedCriteriaId);
    if (criteria?.policyId) {
      loadPolicy(criteria.policyId);
    }
    generate(selectedCriteriaId);
  }, [selectedCriteriaId, criteriaList, generate, loadPolicy]);

  const handleSave = async (finalText: string) => {
    if (!suggestion) return;
    const criteriaId = suggestion.criteriaId;
    await acceptModerationSuggestion(suggestion.id, finalText);

    const criteria = await fetchCriteria();
    setCriteriaList(criteria);
    const c = criteria.find(c => c.id === criteriaId);
    if (c?.policyId) {
      loadPolicy(c.policyId);
    }
    setSuggestion(null);
    setScreen('select');
  };

  const handleFlagSelected = async () => {
    if (selectedToFlag.size === 0 || flagging) return;
    const previousSuggestion = suggestion;
    const previousScreen = screen;
    setFlagging(true);
    setSuggestion(null);
    setScreen('select');
    try {
      await flagInaccurateModerations(Array.from(selectedToFlag));
    } catch {
      setSuggestion(previousSuggestion);
      setScreen(previousScreen);
      setFlagging(false);
      setGenerateError('Failed to flag feedback. Please try again.');
      return;
    }
    setSelectedToFlag(new Set());
    setFlagging(false);
    if (selectedCriteriaId) {
      generate(selectedCriteriaId);
    }
  };

  if (loading) {
    return null;
  }

  return (
    <SuggestionPageShell
      title="Moderation Policy Suggestions"
      criteriaList={criteriaList}
      selectedCriteriaId={selectedCriteriaId}
      onCriteriaChange={id => {
        setSelectedCriteriaId(id);
        setScreen('select');
      }}
      placeholderText="Select a criterion to generate suggestions from moderation feedback"
      generating={generating}
      generateError={generateError}
      hasSuggestion={!!suggestion && screen !== 'select'}
    >
      {suggestion && screen === 'contradiction' && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            height: 'calc(100% - 56px)',
            minHeight: 0,
            maxWidth: 1200,
            mx: 'auto',
          }}
        >
          <ContradictionAlert reason={suggestion.contradictionReason} />

          <Box sx={{ textAlign: 'center', position: 'relative' }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Supporting Moderations ({suggestion.linkedModerations.length})
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: 1.5,
                overflowX: 'auto',
                pb: 1,
              }}
            >
              {suggestion.linkedModerations.map((mod, i) => (
                <ModerationCard
                  key={mod.airtableRecordId || i}
                  moderation={mod}
                  selected={selectedToFlag.has(mod.airtableRecordId)}
                  onToggleSelected={toggleSelectedToFlag}
                  onClick={() => handleOpenDialog(mod)}
                />
              ))}
            </Box>
            <FlagSelectedBar
              count={selectedToFlag.size}
              flagging={flagging}
              onFlag={handleFlagSelected}
            />
          </Box>

          <Box sx={{ flex: 1, minHeight: 300, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Policy
            </Typography>
            {currentPolicy && (
              <HighlightedPolicy
                policyText={currentPolicy.text}
                conflictingText={suggestion.conflictingText}
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, pb: 1 }}>
            <Button variant="contained" startIcon={<EditIcon />} onClick={() => setScreen('diff')}>
              Update Policy
            </Button>
          </Box>
        </Box>
      )}

      {suggestion && screen === 'diff' && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            height: 'calc(100% - 56px)',
            maxWidth: 1200,
            mx: 'auto',
          }}
        >
          {suggestion.linkedModerations.length > 0 && (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Feedback ({suggestion.linkedModerations.length})
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  overflowX: 'auto',
                  pb: 1,
                }}
              >
                {suggestion.linkedModerations.map((mod, i) => (
                  <ModerationCard
                    key={mod.airtableRecordId || i}
                    moderation={mod}
                    selected={selectedToFlag.has(mod.airtableRecordId)}
                    onToggleSelected={toggleSelectedToFlag}
                    onClick={() => handleOpenDialog(mod)}
                  />
                ))}
              </Box>
              <FlagSelectedBar
                count={selectedToFlag.size}
                flagging={flagging}
                onFlag={handleFlagSelected}
              />
            </Box>
          )}

          <Box sx={{ flex: 1, minHeight: 300, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Policy Suggestions
            </Typography>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              {currentPolicy && (
                <SuggestionReview
                  original={currentPolicy.text}
                  suggestions={suggestion.suggestedEdits}
                  onBack={() => setScreen('contradiction')}
                  onSave={handleSave}
                />
              )}
            </Box>
          </Box>
        </Box>
      )}

      <Dialog open={!!dialogModeration} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Sentence Classifications
            <Chip label="Positive" size="small" color="success" variant="outlined" />
            <Chip label="Negative" size="small" color="error" variant="outlined" />
            <Chip label="Both" size="small" variant="outlined" sx={PURPLE_SX} />
          </Box>
          <IconButton onClick={handleCloseDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {dialogModeration?.contextForChange && (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                bgcolor: 'rgba(255, 193, 7, 0.05)',
                borderLeft: '3px solid',
                borderColor: 'warning.main',
                borderRadius: 1,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}
              >
                Moderator Feedback
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {dialogModeration.contextForChange}
              </Typography>
            </Box>
          )}
          {dialogModeration?.originalComment && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'rgba(0, 0, 0, 0.03)', borderRadius: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}
              >
                AI Comment
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {dialogModeration.originalComment}
              </Typography>
            </Box>
          )}
          {dialogModeration?.sentences && dialogModeration.sentences.length > 0 ? (
            <>
              <Box sx={{ mb: selectedSentence ? 2 : 0 }}>
                <EssayContent
                  fragments={dialogModeration.sentences.map(s => s.sentence)}
                  selectedFragmentIndex={selectedSentenceIndex}
                  handleFragmentClick={handleSentenceClick}
                  getLabelTypeForFragment={getLabelTypeForFragment}
                />
              </Box>
              {selectedSentence && (
                <Box sx={{ pt: 1, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
                  {selectedSentence.label && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="subtitle2">Classification:</Typography>
                      <ClassificationChip value={selectedSentence.label} />
                    </Box>
                  )}
                  {selectedSentence.reason && (
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        bgcolor: 'rgba(0, 0, 0, 0.03)',
                        borderLeft: '3px solid rgba(0, 0, 0, 0.2)',
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}
                      >
                        AI reasoning
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedSentence.reason}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </>
          ) : (
            <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No judgement data found.
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </SuggestionPageShell>
  );
};

export default ModerationPolicySuggestions;
